<template>
  <div class="container-central">
    <h1 class="mt-5 mb-4">BLOQUEOS DE CALENDARIO</h1>

    <div v-if="loadingPerfil" class="text-center my-5">
      <b-spinner class="align-middle"></b-spinner>
      <strong style="margin-left: 5px">Cargando perfil...</strong>
    </div>

    <div v-else-if="!perfilSesionista" class="text-center my-5">
      <b-alert show variant="warning">
        No tienes un perfil de sesionista activo. Solo los sesionistas pueden gestionar bloqueos de calendario.
      </b-alert>
    </div>

    <template v-else>
      <!-- Formulario para crear bloqueo -->
      <b-card class="mb-4">
        <template #header>
          <strong>Crear nuevo bloqueo</strong>
        </template>
        <b-form @submit.prevent="crearBloqueo">
          <b-form-group label="Tipo de bloqueo *" label-for="tipo">
            <b-form-select id="tipo" v-model="form.tipo" :options="tipoOpciones"></b-form-select>
          </b-form-group>

          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Fecha inicio *" label-for="fecha_inicio">
                <b-form-input id="fecha_inicio" type="date" v-model="form.fecha_inicio" required></b-form-input>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Fecha fin *" label-for="fecha_fin">
                <b-form-input id="fecha_fin" type="date" v-model="form.fecha_fin" required></b-form-input>
              </b-form-group>
            </div>
          </div>

          <div class="row" v-if="form.tipo === 'hora'">
            <div class="col-md-6">
              <b-form-group label="Hora inicio *" label-for="hora_inicio">
                <b-form-select id="hora_inicio" v-model="form.hora_inicio" :options="opcionesHoras"></b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Hora fin *" label-for="hora_fin">
                <b-form-select id="hora_fin" v-model="form.hora_fin" :options="opcionesHoras"></b-form-select>
              </b-form-group>
            </div>
          </div>

          <b-form-group label="Mensaje personalizado (opcional)" label-for="mensaje">
            <b-form-textarea
              id="mensaje"
              v-model="form.mensaje"
              placeholder="Ej: Estoy de vacaciones, regreso el 15 de enero"
              rows="2"
            ></b-form-textarea>
          </b-form-group>

          <div class="d-flex justify-content-end">
            <b-button type="submit" variant="primary" :disabled="!formValido || creando">
              <b-spinner v-if="creando" small class="mr-1"></b-spinner>
              {{ creando ? 'Creando...' : 'Crear bloqueo' }}
            </b-button>
          </div>
        </b-form>
      </b-card>

      <!-- Lista de bloqueos -->
      <b-card>
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <strong>Mis bloqueos</strong>
            <b-spinner v-if="loadingBloqueos" small></b-spinner>
          </div>
        </template>

        <b-table
          striped
          hover
          :items="bloqueos"
          :fields="camposTabla"
          :busy="loadingBloqueos"
          show-empty
          empty-text="No tienes bloqueos configurados"
          responsive="sm"
        >
          <template #cell(tipo)="data">
            <b-badge :variant="data.item.tipo === 'dia' ? 'danger' : 'warning'">
              {{ data.item.tipo === 'dia' ? 'Día completo' : 'Horas específicas' }}
            </b-badge>
          </template>
          <template #cell(fechas)="data">
            <span>{{ data.item.fecha_inicio }} / {{ data.item.fecha_fin }}</span>
          </template>
          <template #cell(horas)="data">
            <span v-if="data.item.tipo === 'hora'">
              {{ secondsToHM(data.item.hora_inicio) }} - {{ secondsToHM(data.item.hora_fin) }}
            </span>
            <span v-else class="text-muted">-</span>
          </template>
          <template #cell(mensaje)="data">
            <span v-if="data.item.mensaje">{{ data.item.mensaje }}</span>
            <span v-else class="text-muted">Sin mensaje</span>
          </template>
          <template #cell(estado_bloqueo)="data">
            <b-badge :variant="esBloqueoPasado(data.item) ? 'secondary' : 'success'">
              {{ esBloqueoPasado(data.item) ? 'Pasado' : 'Activo' }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <b-icon
              icon="trash"
              variant="danger"
              @click="confirmarEliminar(data.item)"
              scale="1.5"
              style="cursor: pointer"
              v-b-tooltip.hover
              title="Eliminar"
            ></b-icon>
          </template>
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong style="margin-left: 5px">Cargando...</strong>
            </div>
          </template>
        </b-table>
      </b-card>
    </template>
  </div>
</template>

<script>
import SesionesService from "@/services/sesiones.service";
import EventBus from "@/services/eventBus.js";
import { Helpers } from "@/mixins/Helpers";

export default {
  name: "CalendarioBloqueos",
  mixins: [Helpers],
  data() {
    return {
      sesionesService: null,
      perfilSesionista: null,
      loadingPerfil: true,
      bloqueos: [],
      loadingBloqueos: false,
      creando: false,
      from: "calendariobloqueos",
      itemSeleted: null,
      form: {
        tipo: "dia",
        fecha_inicio: "",
        fecha_fin: "",
        hora_inicio: null,
        hora_fin: null,
        mensaje: "",
      },
      tipoOpciones: [
        { value: "dia", text: "Día completo" },
        { value: "hora", text: "Horas específicas" },
      ],
      camposTabla: [
        { key: "tipo", label: "Tipo", sortable: true },
        { key: "fechas", label: "Fechas", sortable: true },
        { key: "horas", label: "Horas", sortable: false },
        { key: "mensaje", label: "Mensaje", sortable: false },
        { key: "estado_bloqueo", label: "Estado", sortable: false },
        { key: "actions", label: "Acciones", sortable: false },
      ],
    };
  },
  computed: {
    formValido() {
      if (!this.form.fecha_inicio || !this.form.fecha_fin) return false;
      if (this.form.fecha_fin < this.form.fecha_inicio) return false;
      if (this.form.tipo === "hora") {
        if (this.form.hora_inicio === null || this.form.hora_fin === null) return false;
        if (this.form.hora_fin <= this.form.hora_inicio) return false;
      }
      return true;
    },
    opcionesHoras() {
      const opciones = [];
      for (let i = 0; i < 24 * 60; i += 30) {
        opciones.push({
          value: i * 60,
          text: this.secondsToHM(i * 60),
        });
      }
      return opciones;
    },
  },
  created() {
    this.sesionesService = new SesionesService();
  },
  mounted() {
    this.cargarPerfil();
    EventBus.$on("RESPONSE_MODAL_QUESTION", (payLoad) => {
      if (payLoad.from === this.from && payLoad.action === "delete") {
        this.ejecutarEliminar();
      }
    });
  },
  beforeDestroy() {
    EventBus.$off("RESPONSE_MODAL_QUESTION");
  },
  methods: {
    cargarPerfil() {
      this.loadingPerfil = true;
      this.sesionesService
        .getMiPerfilSesionista()
        .then((res) => {
          if (res.data.status === 200) {
            this.perfilSesionista = res.data.data;
            this.cargarBloqueos();
          } else {
            this.perfilSesionista = null;
          }
        })
        .catch(() => {
          this.perfilSesionista = null;
        })
        .finally(() => {
          this.loadingPerfil = false;
        });
    },
    cargarBloqueos() {
      this.loadingBloqueos = true;
      this.sesionesService
        .getMisBloqueos()
        .then((res) => {
          this.bloqueos = res.data.data || [];
        })
        .catch(() => {
          EventBus.$emit("MOSTRAR_TOAST", {
            title: "Error",
            variant: "danger",
            message: "No se pudieron cargar los bloqueos",
          });
        })
        .finally(() => {
          this.loadingBloqueos = false;
        });
    },
    crearBloqueo() {
      if (!this.formValido) return;
      this.creando = true;
      const payload = {
        tipo: this.form.tipo,
        fecha_inicio: this.form.fecha_inicio,
        fecha_fin: this.form.fecha_fin,
        hora_inicio: this.form.tipo === "hora" ? this.form.hora_inicio : null,
        hora_fin: this.form.tipo === "hora" ? this.form.hora_fin : null,
        mensaje: this.form.mensaje || null,
      };
      this.sesionesService
        .crearBloqueo(payload)
        .then((res) => {
          if (res.data.status === 200) {
            EventBus.$emit("MOSTRAR_TOAST", {
              title: "Bloqueo creado",
              variant: "success",
              message: "El bloqueo se ha creado correctamente",
            });
            this.resetForm();
            this.cargarBloqueos();
          }
        })
        .catch(() => {
          EventBus.$emit("MOSTRAR_TOAST", {
            title: "Error",
            variant: "danger",
            message: "Error al crear el bloqueo",
          });
        })
        .finally(() => {
          this.creando = false;
        });
    },
    confirmarEliminar(item) {
      this.itemSeleted = { ...item };
      EventBus.$emit("SHOW_MODAL_QUESTION", {
        titleModal: "Eliminar bloqueo",
        messageModal: "¿Seguro desea eliminar este bloqueo de calendario?",
        action: "delete",
        from: this.from,
      });
    },
    ejecutarEliminar() {
      if (!this.itemSeleted) return;
      this.sesionesService
        .eliminarBloqueo(this.itemSeleted.id)
        .then((res) => {
          if (res.data.status === 200) {
            this.bloqueos = this.bloqueos.filter(
              (b) => b.id !== this.itemSeleted.id
            );
            this.itemSeleted = null;
            EventBus.$emit("MOSTRAR_TOAST", {
              title: "Eliminado",
              variant: "success",
              message: "Bloqueo eliminado correctamente",
            });
          }
        })
        .catch(() => {
          EventBus.$emit("MOSTRAR_TOAST", {
            title: "Error",
            variant: "danger",
            message: "Error al eliminar el bloqueo",
          });
        });
    },
    resetForm() {
      this.form.tipo = "dia";
      this.form.fecha_inicio = "";
      this.form.fecha_fin = "";
      this.form.hora_inicio = null;
      this.form.hora_fin = null;
      this.form.mensaje = "";
    },
    esBloqueoPasado(item) {
      const hoy = new Date().toISOString().split("T")[0];
      return item.fecha_fin < hoy;
    },
  },
};
</script>

<style lang="scss" scoped>
.container-central {
  width: 100%;
  min-height: 700px;
  padding: 0 20px 40px;
}
</style>
